import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSensors } from '../../hooks/useSensors';
import { useSensorNames } from '../../context/SensorNamesContext';
import SensorMap from './SensorMap';
import SensorModal from './SensorModal';
import { Sensor } from '../../services/sensors';
import { PARAM_CONFIG, DEPTHS, ParamKey, getDepthThreshold, depthsFromMetrics } from '../../data/demo';
import { ParamIcon } from './ParamIcon';
import { markerLabel } from '../../utils/deviceMarker';

function getParamStatus(value: number, lo: number, hi: number): 'good' | 'warn' | 'alert' {
  const margin = (hi - lo) * 0.15;
  if (value < lo - margin || value > hi + margin) return 'alert';
  if (value < lo || value > hi) return 'warn';
  return 'good';
}

/**
 * null y NO 0 en las profundidades sin lectura. `awaitingData` sólo cubre el
 * caso "el sensor nunca reportó NADA"; un sensor que sí reporta pero no ESTE
 * parámetro caía igual al cero y pintaba las 7 marcas en rojo con "Prom.:
 * 0.0" — indistinguible de una medición real de cero, que en este piloto son
 * legítimas. Mismo criterio ya aplicado en SensorModal.
 */
function getDepthValues(metrics: Record<string, number>, paramKey: ParamKey, depths: number[]): Array<number | null> {
  return depths.map(d => metrics[`${paramKey}_${d}`] ?? null);
}

export default function SoilOverview() {
  const { t } = useTranslation();
  const { sensors, loading, refresh } = useSensors();
  const { getName, rename, pendientesDe, subirPendientes, descartarPendientes } = useSensorNames();
  const [subiendoNombres, setSubiendoNombres] = useState(false);
  const [falloNombres, setFalloNombres] = useState(false);

  /**
   * Nombres que quedaron guardados sólo en este navegador, de cuando
   * renombrar no llegaba al servidor. Se ofrecen subir en vez de tirarlos:
   * para quien los puso, ésos son los nombres buenos.
   */
  const nombresPendientes = pendientesDe(sensors);

  const subirNombres = async () => {
    setSubiendoNombres(true);
    setFalloNombres(false);
    try {
      await subirPendientes(sensors);
      await refresh();
    } catch {
      setFalloNombres(true);
    } finally {
      setSubiendoNombres(false);
    }
  };
  const [activeParam, setActiveParam] = useState<ParamKey>('humidity');
  const [selectedSensor, setSelectedSensor] = useState<Sensor | null>(null);
  const [rightTab, setRightTab] = useState<'sensors' | 'alerts'>('sensors');

  const paramCfg = PARAM_CONFIG.find(p => p.key === activeParam)!;

  const optimal = sensors.filter(s => s.status === 'good').length;
  const warning = sensors.filter(s => s.status === 'warn').length;
  const alert   = sensors.filter(s => s.status === 'alert').length;

  const markers = sensors.map(s => ({
    id: s.id,
    label: markerLabel(s.type),
    title: getName(s.imei, s.name),
    lat: s.lat,
    lng: s.lng,
    status: s.status as 'good' | 'warn' | 'alert' | 'offline',
  }));

  // El centro estaba fijo en [32.5756, -83.8675] — la finca demo de Georgia.
  // Un cliente de México abría Suelo y veía campos de Estados Unidos aunque
  // sus sensores estuvieran en Monterrey. Ahora sigue a los sensores reales.
  const validMarkers = markers.filter(m => m.lat != null && m.lng != null);
  const mapCenter: [number, number] = validMarkers.length > 0
    ? [
        validMarkers.reduce((a, m) => a + Number(m.lat), 0) / validMarkers.length,
        validMarkers.reduce((a, m) => a + Number(m.lng), 0) / validMarkers.length,
      ]
    : [32.5756, -83.8675];

  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>

      {/* ── Left: Parameter filter ───────────────────────────── */}
      <div style={{ width: 176, borderRight: '1px solid var(--border)', display: 'flex', flexDirection: 'column', flexShrink: 0, background: 'var(--white)' }}>
        <div style={{ padding: '14px 14px 10px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--dark)' }}>{t('soil.title')}</div>
          <div style={{ fontSize: 10, color: 'var(--gray)', marginTop: 2 }}>
            {loading ? t('soil.loading') : t('soil.activeSensors', { count: sensors.length })}
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '6px 6px' }}>
          {PARAM_CONFIG.map(p => (
            <ParamItem
              key={p.key}
              icon={p.icon}
              label={t(`soil.params.${p.key}`)}
              active={activeParam === p.key}
              onClick={() => setActiveParam(p.key as ParamKey)}
            />
          ))}
        </div>
      </div>

      {/* ── Center: Header bar + Map ─────────────────────────── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Parameter header bar */}
        <div style={{ background: '#0d1f0f', color: '#fff', padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 18 }}>{paramCfg.icon}</span>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{t(`soil.params.${activeParam}`)}</div>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)' }}>{t('soil.colorLegend')}</div>
            </div>
          </div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', textAlign: 'right' }}>
            <div>Shallow: {paramCfg.shallowRange}</div>
            <div>Deep: {paramCfg.deepRange}</div>
          </div>
        </div>

        {/* Map */}
        <div style={{ flex: 1, position: 'relative' }}>
          <SensorMap
            center={mapCenter}
            zoom={15}
            markers={markers}
            onMarkerClick={id => setSelectedSensor(sensors.find(s => s.id === id) ?? null)}
          />
        </div>
      </div>

      {/* ── Right: Stats + Sensor list ───────────────────────── */}
      <div style={{ width: 300, borderLeft: '1px solid var(--border)', display: 'flex', flexDirection: 'column', background: 'var(--white)', flexShrink: 0 }}>
        {/* Nombres atrapados en este navegador — ver SensorNamesContext. */}
        {nombresPendientes.length > 0 && (
          <div style={{ margin: '10px 10px 0', padding: '11px 12px', borderRadius: 10, border: '1.5px solid var(--amber)', background: '#fffbeb' }}>
            <div style={{ fontSize: 11.5, color: 'var(--dark)', lineHeight: 1.5, marginBottom: 8 }}>
              {nombresPendientes.length === 1 ? 'One sensor name is' : `${nombresPendientes.length} sensor names are`} saved
              only in this browser and never reached the server — everyone else still sees the IMEI.
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                onClick={subirNombres}
                disabled={subiendoNombres}
                style={{
                  fontSize: 11, fontWeight: 600, color: 'var(--white)', background: 'var(--green)',
                  border: 'none', borderRadius: 6, padding: '5px 10px',
                  cursor: subiendoNombres ? 'default' : 'pointer', opacity: subiendoNombres ? 0.6 : 1,
                }}
              >
                {subiendoNombres ? 'Saving…' : 'Save to server'}
              </button>
              <button
                onClick={descartarPendientes}
                disabled={subiendoNombres}
                style={{
                  fontSize: 11, color: 'var(--gray)', background: 'var(--white)',
                  border: '1px solid var(--border)', borderRadius: 6, padding: '5px 10px',
                  cursor: subiendoNombres ? 'default' : 'pointer',
                }}
              >
                {t('locations.discard')}
              </button>
            </div>
            {falloNombres && (
              <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 7 }}>
                {t('locations.saveFailed')}
              </div>
            )}
          </div>
        )}
        {/* KPI bubbles */}
        <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 12, justifyContent: 'center' }}>
          <KPIBubble count={optimal} label={t('status.device.good')}  color="var(--green)" />
          <KPIBubble count={warning} label={t('status.device.warn')}  color="var(--amber)" />
          <KPIBubble count={alert}   label={t('status.device.alert')} color="var(--red)" />
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--border)' }}>
          {(['sensors', 'alerts'] as const).map(tab => (
            <button
              key={tab}
              onClick={() => setRightTab(tab)}
              style={{
                flex: 1, padding: '9px 0', border: 'none', background: 'transparent', fontSize: 12,
                fontWeight: rightTab === tab ? 600 : 400,
                color: rightTab === tab ? 'var(--green-dk)' : 'var(--gray)',
                borderBottom: rightTab === tab ? '2px solid var(--green)' : '2px solid transparent',
                cursor: 'pointer',
              }}
            >
              {tab === 'sensors' ? t('soil.sensors') : t('soil.alerts')}
            </button>
          ))}
        </div>

        {/* Sensor cards */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px' }}>
          {(() => {
            const visible = rightTab === 'alerts'
              ? sensors.filter(s => s.status === 'warn' || s.status === 'alert')
              : sensors;
            if (visible.length === 0) return (
              <div style={{ padding: 20, textAlign: 'center', fontSize: 12, color: 'var(--gray)' }}>
                {rightTab === 'alerts' ? 'No warnings or alerts' : t('soil.noSensors')}
              </div>
            );
            return visible.map(s => (
              <SensorCard
                key={s.id}
                sensor={s}
                displayName={getName(s.imei, s.name)}
                paramKey={activeParam}
                paramCfg={paramCfg}
                selected={selectedSensor?.id === s.id}
                onSelect={() => setSelectedSensor(s)}
              />
            ));
          })()}
        </div>
      </div>

      {/* Modal */}
      {selectedSensor && (
        <SensorModal
          sensor={selectedSensor}
          displayName={getName(selectedSensor.imei, selectedSensor.name)}
          initialParam={activeParam}
          onClose={() => setSelectedSensor(null)}
          onRename={async name => {
            await rename(selectedSensor, name);
            await refresh();
          }}
        />
      )}
    </div>
  );
}

function ParamItem({ icon, label, active, onClick }: { icon: string; label: string; active: boolean; onClick: () => void }) {
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '7px 8px', borderRadius: 6, cursor: 'pointer',
        marginBottom: 1, background: active ? 'var(--green-bg)' : 'transparent', transition: 'background 0.12s',
      }}
      onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'var(--bg)'; }}
      onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
    >
      <ParamIcon icon={icon} size={16} />
      <span style={{ fontSize: 12, color: active ? 'var(--green-dk)' : 'var(--dark)', fontWeight: active ? 600 : 400 }}>{label}</span>
      {active && <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--green)', marginLeft: 'auto', flexShrink: 0 }} />}
    </div>
  );
}

function KPIBubble({ count, label, color }: { count: number; label: string; color: string }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: 20, fontWeight: 700, color }}>{count}</div>
      <div style={{ fontSize: 10, color: 'var(--gray)' }}>{label}</div>
    </div>
  );
}

function SensorCard({
  sensor, displayName, paramKey, paramCfg, selected, onSelect,
}: {
  sensor: Sensor;
  displayName: string;
  paramKey: ParamKey;
  paramCfg: typeof PARAM_CONFIG[number];
  selected: boolean;
  onSelect: () => void;
}) {
  const { t } = useTranslation();

  // Un device dado de alta que todavía no reporta no tiene métricas: sin este
  // caso, `getDepthValues` devolvía ceros y la fila salía como "Alert" con
  // "Avg: 0.0 %" — idéntico a un sensor midiendo cero de verdad. Se muestra
  // igual en la lista (confirma que quedó registrado) pero como "sin datos".
  const awaitingData = !sensor.lastReading;
  const metrics = sensor.lastReading?.metrics ?? {};
  const sensorDepths = depthsFromMetrics(metrics);
  const values = getDepthValues(metrics, paramKey, sensorDepths);
  const present = values.filter((v): v is number => v != null);
  const avg = present.length > 0 ? present.reduce((a, b) => a + b, 0) / present.length : null;
  const statuses = values.map((v, i) => {
    if (v == null) return null;
    const { lo, hi } = getDepthThreshold(paramCfg, i);
    return getParamStatus(v, lo, hi);
  });

  const statusColor = awaitingData
    ? 'var(--gray)'
    : sensor.status === 'good' ? 'var(--green-dk)' : sensor.status === 'warn' ? 'var(--amber)' : 'var(--red)';
  // "Desconectado" a secas no dice si el equipo lleva 3 horas o 3 días mudo, y
  // esa diferencia es justo la que distingue un bache de red de un sensor que
  // dejó de publicar. Sin el dato en pantalla, averiguarlo obliga a ir a MQTT
  // Logs del panel de admin — que el cliente ni siquiera puede ver.
  const lastReadingAt = sensor.lastReading ? new Date(sensor.lastReading.receivedAt) : null;
  const staleFor = lastReadingAt ? Date.now() - lastReadingAt.getTime() : null;

  const statusLabel = awaitingData
    ? t('soil.awaitingData')
    : t(`status.device.${sensor.status}`);

  return (
    <div
      onClick={onSelect}
      style={{
        padding: '12px', borderRadius: 10,
        border: selected ? '1.5px solid var(--green)' : '1px solid var(--border)',
        background: selected ? 'var(--green-bg)' : 'var(--white)',
        marginBottom: 8, cursor: 'pointer', transition: 'all 0.15s',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 6 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--dark)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
          {displayName}
        </span>
        <span style={{ fontSize: 10, fontWeight: 600, color: statusColor, flexShrink: 0 }}>{statusLabel}</span>
      </div>

      {sensor.status === 'offline' && staleFor != null && (
        <div style={{ fontSize: 10, color: 'var(--red)', marginBottom: 6 }}>
          {t('soil.lastSignal', { ago: formatAgo(staleFor, t) })}
        </div>
      )}

      {!awaitingData && <DepthTickBar statuses={statuses} depths={sensorDepths} />}

      <div style={{ fontSize: 11, color: 'var(--gray)', marginTop: 6 }}>
        {awaitingData ? (
          t('soil.noReadingsYet')
        ) : (
          <>
            {t('soil.avg')}: <span style={{ fontWeight: 600, color: avg != null && avg > 0 ? 'var(--green-dk)' : 'var(--gray)' }}>
              {avg != null ? `${avg.toFixed(1)} ${paramCfg.unit}` : '—'}
            </span>
          </>
        )}
      </div>
    </div>
  );
}

// `null` en statuses = esa profundidad no reportó este parámetro. Se pinta
// gris, no roja: una marca de alerta sobre un dato que no existe es una
// afirmación falsa sobre el suelo del cliente.
function DepthTickBar({ statuses, depths = DEPTHS }: { statuses: Array<'good' | 'warn' | 'alert' | null>; depths?: number[] }) {
  const COLOR = { good: 'var(--green)', warn: 'var(--amber)', alert: 'var(--red)' };
  return (
    <svg width="100%" height="20" viewBox="0 0 240 20">
      {/* baseline */}
      <line x1="0" y1="14" x2="240" y2="14" stroke="var(--border)" strokeWidth="1.5" />
      {/* depth labels + ticks */}
      {depths.map((d, i) => {
        const x = (i / Math.max(1, depths.length - 1)) * 230 + 5;
        return (
          <g key={d}>
            <text x={x} y="20" fontSize="7" fill="var(--gray)" textAnchor="middle">{d}</text>
            <rect x={x - 3} y="6" width="6" height="8" rx="1" fill={statuses[i] ? COLOR[statuses[i]!] : 'var(--border)'} />
          </g>
        );
      })}
    </svg>
  );
}

/**
 * "hace 3 h", "hace 2 d". Redondeo hacia abajo a propósito: exagerar la
 * frescura de un dato es peor que quedarse corto.
 */
function formatAgo(ms: number, t: (k: string, o?: Record<string, unknown>) => string): string {
  const min = Math.floor(ms / 60000);
  if (min < 60) return t('soil.ago.minutes', { n: min });
  const h = Math.floor(min / 60);
  if (h < 48) return t('soil.ago.hours', { n: h });
  return t('soil.ago.days', { n: Math.floor(h / 24) });
}
